/**
 * MOCK · ASSUMED (CT-5717) — a record's OWN drive on the Knowledge tab: its
 * folders and files, whether Concierge reads them for claims, and the folders
 * the user creates. Not the vault picker of note attachments (`drives.js`,
 * CT-5528): a different resource.
 *
 * The seed is the prototype's (record PER-0104): the drive root with the
 * "Renewal playbook" folder and ten files, and that folder's two files — 12
 * files, 10 read. Worded with the record's name.
 *
 * The claims a file holds are the Sandbox's (CT-5718): Drive counts them from
 * the Sandbox seed, so "View claims" lands on exactly that many. The QA switch
 * (`?mockKnowledge=…`) and the shared file dates live in `knowledgeSeed.mock.js`.
 */

import { getMockEntityHeaders } from './entityHeader.mock.js';
import { __setMockKnowledgeScenario, getMockKnowledgeScenario, KNOWLEDGE_FILE_DATES } from './knowledgeSeed.mock.js';
import { countMockSandboxClaimsByFile } from './sandbox.mock.js';

export { getMockKnowledgeScenario, KNOWLEDGE_SCENARIOS, __setMockKnowledgeScenario } from './knowledgeSeed.mock.js';

const DAY_MS = 24 * 60 * 60 * 1000;
const ROOT = 'root';

/** Per record: folders created and extraction switches set by the user. */
let state = new Map();

const recordKey = (entityType, entityId) => `${entityType}/${entityId}`;
const recordState = (entityType, entityId) => {
  const key = recordKey(entityType, entityId);
  if (!state.has(key)) state.set(key, { folders: [], extraction: {} });
  return state.get(key);
};

const nameOf = (entityType, entityId) => {
  const rows = getMockEntityHeaders()[entityType] ?? [];
  const row = rows.find((candidate) => (candidate.entity_id ?? candidate.entityId ?? candidate.id) === entityId);
  return row?.title ?? row?.name ?? 'this contact';
};

const daysAgo = (days) => new Date(Date.now() - days * DAY_MS).toISOString();

const file = (fields) => ({ kind: 'file', read: true, status: [], ...fields });

/** The claims each file holds, as the Sandbox counts them (CT-5718). */
const withClaimCounts = (files, counts) => files.map((row) => ({ ...row, claims_extracted: counts[row.id] ?? 0 }));

/** Every file of the drive, each in its folder (`parent_id`). */
const buildFiles = (name) => [
  file({ id: 'file-runbook', parent_id: ROOT, name: 'Cut-over runbook (Google Doc)', type: 'doc', created_by: 'Daniel Reyes', source_line: 'Google Docs · edited 12m ago by Daniel Reyes', size_bytes: null, occurred_at: daysAgo(0), status: ['live'] }),
  file({ id: 'file-sq-answers', parent_id: ROOT, name: 'Security-questionnaire-answers.docx', type: 'doc', created_by: 'Priya Nair', source_line: "Moved from Meridian Corp's drive by Priya Nair", size_bytes: 64 * 1024, occurred_at: daysAgo(9), status: ['moved'] }),
  file({ id: 'file-migration', parent_id: ROOT, name: 'Migration-plan-v2.pdf', type: 'pdf', created_by: 'Daniel Reyes', source_line: "Also on Daniel Reyes's record · the original stays there", size_bytes: 410 * 1024, occurred_at: daysAgo(11), status: ['linked'] }),
  file({ id: 'file-call-transcript', parent_id: ROOT, name: 'Call-transcript-2026-09-02.txt', type: 'txt', created_by: 'Read AI', source_line: 'Read AI · Call · Renewal follow-up', size_bytes: 18 * 1024, occurred_at: KNOWLEDGE_FILE_DATES['file-call-transcript'], status: ['meeting_record', 'recording'] }),
  file({ id: 'file-qbr', parent_id: ROOT, name: 'QBR-transcript-aug26.txt', type: 'txt', created_by: 'Concierge', source_line: 'Note · QBR: two escalations with no resolution date', size_bytes: 41 * 1024, occurred_at: KNOWLEDGE_FILE_DATES['file-qbr'], status: ['meeting_record', 'recording'] }),
  file({ id: 'file-sq-draft', parent_id: ROOT, name: 'Security-questionnaire-draft.xlsx', type: 'sheet', created_by: 'Priya Nair', source_line: 'Note · Security review prep — what we owe and by when', size_bytes: 96 * 1024, occurred_at: daysAgo(85), status: ['note'] }),
  file({ id: 'file-access-log', parent_id: ROOT, name: 'Access-review-log-Q3.pdf', type: 'pdf', created_by: 'Priya Nair', source_line: 'Email · Evidence pack delivered', size_bytes: 1.2 * 1024 * 1024, occurred_at: KNOWLEDGE_FILE_DATES['file-access-log'], status: ['email'] }),
  file({ id: 'file-pentest', parent_id: ROOT, name: 'Pen-test-summary-2026.pdf', type: 'pdf', created_by: 'Priya Nair', source_line: 'Email · Evidence pack delivered', size_bytes: 860 * 1024, occurred_at: daysAgo(89), status: ['email'] }),
  file({ id: 'file-soc2', parent_id: ROOT, name: 'SOC2-bridge-letter.pdf', type: 'pdf', created_by: 'Priya Nair', source_line: 'Email · Evidence pack delivered', size_bytes: 140 * 1024, occurred_at: daysAgo(89), status: ['email'] }),
  file({ id: 'file-agenda', parent_id: ROOT, name: 'Kick-off-agenda.docx', type: 'doc', created_by: 'Priya Nair', source_line: 'Note · Kick-off agenda', size_bytes: 22 * 1024, occurred_at: daysAgo(120), status: ['note'], read: false }),
  file({ id: 'file-proposal', parent_id: 'f-playbook', name: 'Proposal-draft-Meridian.docx', type: 'doc', created_by: 'Concierge', source_line: 'Written by Concierge · draft, not approved', size_bytes: 52 * 1024, occurred_at: daysAgo(6), status: ['concierge'], read: false }),
  file({ id: 'file-kickoff', parent_id: 'f-playbook', name: `Renewal-kickoff-${name}.docx`, type: 'doc', created_by: 'Priya Nair', source_line: 'Renewal playbook · step “Kick-off email”', size_bytes: 38 * 1024, occurred_at: daysAgo(7), status: ['workflow'] }),
];

const SEED_FOLDERS = [
  {
    id: 'f-playbook',
    parent_id: ROOT,
    name: 'Renewal playbook',
    source_line: 'Created by the Renewal playbook workflow · its steps file here',
    created_by: 'Renewal playbook workflow',
    occurred_at: daysAgo(7),
  },
];

const folderFiles = (files, folderId) => files.filter((row) => row.parent_id === folderId);

/** Files under a folder, its sub-folders included. */
const filesUnder = (files, folders, folderId) => {
  if (folderId === ROOT) return files;
  const ids = new Set([folderId]);
  let grew = true;
  while (grew) {
    grew = false;
    folders.forEach((folder) => {
      if (ids.has(folder.parent_id) && !ids.has(folder.id)) {
        ids.add(folder.id);
        grew = true;
      }
    });
  }
  return files.filter((row) => ids.has(row.parent_id));
};

const QUICK_FILTERS = {
  all: () => true,
  recordings: (row) => row.status.includes('recording'),
  canon: (row) => row.status.includes('canon'),
  with_claims: (row) => row.claims_extracted > 0,
  not_read: (row) => row.kind === 'file' && !row.read,
};

const matchesSearch = (row, search = {}) => {
  const needle = (search.query ?? '').trim().toLowerCase();
  if (!needle) return true;
  const fields = search.fields?.length ? search.fields : ['name'];
  return fields.some((field) => String(row[field] ?? '').toLowerCase().includes(needle));
};

const resolveExtraction = (record, folders, folderId) => {
  let id = folderId;
  while (id) {
    if (record.extraction[id] !== undefined) return { enabled: record.extraction[id], inherited: id !== folderId };
    if (id === ROOT) return { enabled: true, inherited: folderId !== ROOT };
    id = folders.find((folder) => folder.id === id)?.parent_id ?? ROOT;
  }
  return { enabled: true, inherited: folderId !== ROOT };
};

export const listMockEntityDrive = ({
  entityType,
  entityId,
  folderId = ROOT,
  page = 1,
  pageSize = 10,
  orderDirection = 'desc',
  quickFilter = 'all',
  filters = {},
  search,
}) => {
  const scenario = getMockKnowledgeScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock drive failure (?mockKnowledge=fail).' } };

  const name = nameOf(entityType, entityId);
  const driveName = `${name}'s drive`;
  const record = recordState(entityType, entityId);
  const folders = [...SEED_FOLDERS, ...record.folders];
  const files = withClaimCounts(buildFiles(name), countMockSandboxClaimsByFile(name));
  const folder = folders.find((candidate) => candidate.id === folderId);
  const currentId = folder ? folder.id : ROOT;

  const everywhere = (filters.scope ?? []).includes('everywhere');
  const folderRows = everywhere ? [] : folders.filter((candidate) => candidate.parent_id === currentId);
  const fileRows = everywhere ? filesUnder(files, folders, currentId) : folderFiles(files, currentId);

  const asFolderRow = (candidate) => {
    const inside = filesUnder(files, folders, candidate.id);
    return {
      id: candidate.id,
      kind: 'folder',
      name: candidate.name,
      created_by: candidate.created_by ?? null,
      source_line: candidate.source_line ?? null,
      occurred_at: candidate.occurred_at,
      file_count: inside.length,
      claims_extracted: inside.reduce((sum, row) => sum + row.claims_extracted, 0),
      read: inside.every((row) => row.read),
      status: [],
    };
  };

  const types = filters.type ?? [];
  const creators = filters.created_by ?? [];
  const byDate = (a, b) => String(b.occurred_at).localeCompare(String(a.occurred_at));
  const sortedFiles = fileRows
    .filter(QUICK_FILTERS[quickFilter] ?? QUICK_FILTERS.all)
    .filter((row) => !types.length || types.includes(row.type))
    .filter((row) => !creators.length || creators.includes(row.created_by))
    .filter((row) => matchesSearch(row, search))
    .sort(byDate);
  if (orderDirection === 'asc') sortedFiles.reverse();
  const shownFolders =
    quickFilter === 'all' && !types.length && !creators.length ? folderRows.map(asFolderRow).filter((row) => matchesSearch(row, search)) : [];
  const rows = scenario === 'empty' ? [] : [...shownFolders, ...sortedFiles];

  const start = (page - 1) * pageSize;
  // `type` rides along (CT-5717 review): it picks the row's icon, as in the prototype.
  const items = rows.slice(start, start + pageSize).map(({ parent_id: _parent, ...row }) => row);
  const inView = filesUnder(files, folders, currentId);
  const breadcrumb = [{ id: ROOT, name: driveName }, ...(folder ? [{ id: folder.id, name: folder.name }] : [])];

  return {
    body: {
      drive: { name: driveName, owner_label: 'AIMS OS' },
      folder: folder ? { id: folder.id, name: folder.name, source_line: folder.source_line ?? null } : null,
      breadcrumb,
      tree: [
        { id: ROOT, name: driveName, file_count: files.length, depth: 0 },
        ...folders.filter((candidate) => candidate.parent_id === ROOT).map((candidate) => ({
          id: candidate.id,
          name: candidate.name,
          file_count: filesUnder(files, folders, candidate.id).length,
          depth: 1,
        })),
      ],
      extraction: {
        ...resolveExtraction(record, folders, currentId),
        files_read: inView.filter((row) => row.read).length,
        files_total: inView.length,
        claims_total: inView.reduce((sum, row) => sum + row.claims_extracted, 0),
      },
      items,
      // The "Created by" options of All filters: who made a file in this drive.
      created_by_options: [...new Set(files.map((row) => row.created_by))].sort(),
      can_manage: scenario !== 'readonly',
    },
    meta: {
      total_items: rows.length,
      total_pages: Math.max(1, Math.ceil(rows.length / pageSize)),
      current_page: page,
      per_page: pageSize,
      has_next: start + items.length < rows.length,
      has_previous: page > 1,
    },
  };
};

export const setMockDriveExtraction = ({ entityType, entityId, folderId = ROOT, enabled }) => {
  if (getMockKnowledgeScenario() === 'readonly') return { error: { status: 403, description: 'You may not manage this drive.' } };
  recordState(entityType, entityId).extraction[folderId] = Boolean(enabled);
  return { body: { folder_id: folderId, enabled: Boolean(enabled) }, meta: {} };
};

export const createMockDriveFolder = ({ entityType, entityId, parentId = ROOT, name, description, extractClaims }) => {
  const scenario = getMockKnowledgeScenario();
  if (scenario === 'create_fail') return { error: { status: 500, description: 'Mock folder failure (?mockKnowledge=create_fail).' } };
  if (scenario === 'readonly') return { error: { status: 403, description: 'You may not manage this drive.' } };
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return { error: { status: 422, description: 'A folder needs a name.' } };

  const record = recordState(entityType, entityId);
  const siblings = [...SEED_FOLDERS, ...record.folders].filter((folder) => folder.parent_id === parentId);
  if (siblings.some((folder) => folder.name.toLowerCase() === trimmed.toLowerCase())) {
    return { error: { status: 409, description: `There is already a folder called “${trimmed}” here.` } };
  }

  const id = `f-new-${record.folders.length + 1}`;
  record.folders.push({
    id,
    parent_id: parentId,
    name: trimmed,
    source_line: String(description ?? '').trim() || null,
    created_by: 'You',
    occurred_at: new Date().toISOString(),
  });
  // The folder's own setting, whichever way the form's switch was — ON included,
  // so a parent that has it off does not override it (Bugbot, #570).
  record.extraction[id] = extractClaims !== false;
  return { body: { folder_id: id }, meta: {} };
};

export const __resetMockEntityDriveForTests = () => {
  __setMockKnowledgeScenario(null);
  state = new Map();
};
